import { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Alert, AlertTitle, CircularProgress, Chip, Stack, Paper,
  Grid, Divider, Tooltip, LinearProgress,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import DnsIcon from '@mui/icons-material/Dns';
import PlaceIcon from '@mui/icons-material/Place';
import StorageIcon from '@mui/icons-material/Storage';

// 사내 서버 현황 — 어떤 서버가 무엇을 구동하고, 지금 어떤 상태인가.
// 데이터: GET /api/infra/status (cloud-compliance 의 onprem-feed.json)
//
// 🔴 이 화면의 가장 중요한 문장은 맨 위 경고다.
//   "감시 밖에 있는 서버"는 문제가 없어서 조용한 것이 아니라, 문제를 볼 수단이
//   없어서 조용한 것이다. 2026-09-02 실측에서 사외 노출 운영 서비스(RAYFinder)와
//   그 SSOT DB 가 Zabbix 에 아예 등록돼 있지 않았다.
//
// 🔴 모르는 것을 아는 것처럼 그리지 않는다.
//   unmonitored · agent_down 은 절대 초록으로 칠하지 않는다. 회색도 쓰지 않는다 —
//   회색은 "괜찮은데 데이터가 없음"으로 읽힌다. 빨강으로 둔다.
//
// 🔴 CPU·메모리는 7일 평균이다. 순간값이 아니다.
//   순간값으로 판단하면 Hyper-V 호스트처럼 "CPU 는 한가한데 메모리를 상시 점유"
//   하는 장비를 유휴로 오독한다. 실제로 그럴 뻔했다.

const STATE = {
  ok:            { label: '정상',       color: 'success', sev: 0 },
  disk_warning:  { label: '용량 주의',   color: 'warning', sev: 1 },
  disk_critical: { label: '용량 임계',   color: 'error',   sev: 3 },
  agent_down:    { label: '감시 두절',   color: 'error',   sev: 4 },
  unregistered:  { label: '대장 미등재', color: 'error',   sev: 4 },
  unmonitored:   { label: '감시 밖',     color: 'error',   sev: 5 },
};
const CLASS_HINT = {
  L4: '환자 식별 가능 건강정보', L3: '유출 시 사업·법적 손해',
  L2: '사내 한정', L1: '외부 공개 가능',
};

function Meter({ label, value, warn = 70, crit = 85 }) {
  if (value === null || value === undefined) {
    return (
      <Stack sx={{ minWidth: 92 }}>
        <Typography sx={{ fontSize: 10.5, color: 'text.disabled' }}>{label}</Typography>
        <Typography sx={{ fontSize: 13, color: 'text.disabled', fontFamily: 'monospace' }}>—</Typography>
      </Stack>
    );
  }
  const v = typeof value === 'object' ? value.avg : value;
  const mx = typeof value === 'object' ? value.max : null;
  const color = v >= crit ? 'error' : v >= warn ? 'warning' : 'primary';
  return (
    <Tooltip title={mx !== null ? `7일 평균 ${v}% · 최대 ${mx}%` : `7일 평균 ${v}%`}>
      <Stack sx={{ minWidth: 92 }}>
        <Typography sx={{ fontSize: 10.5, color: 'text.disabled' }}>{label}</Typography>
        <Stack direction="row" alignItems="center" spacing={0.8}>
          <Typography sx={{ fontSize: 13, fontFamily: 'monospace', fontWeight: 700 }}>{v}%</Typography>
          <LinearProgress variant="determinate" value={Math.min(v, 100)} color={color}
            sx={{ flex: 1, height: 5, borderRadius: 1 }} />
        </Stack>
      </Stack>
    </Tooltip>
  );
}

function DiskBar({ d }) {
  const color = d.state === 'crit' ? 'error' : d.state === 'warn' ? 'warning' : 'primary';
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ fontSize: 12 }}>
      <StorageIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
      <Typography sx={{ fontSize: 12, fontFamily: 'monospace', minWidth: 58 }}>{d.mount}</Typography>
      <LinearProgress variant="determinate" value={Math.min(d.pused ?? 0, 100)} color={color}
        sx={{ flex: 1, height: 6, borderRadius: 1 }} />
      <Typography sx={{
        fontSize: 12, fontFamily: 'monospace', minWidth: 92, textAlign: 'right',
        color: d.state === 'ok' ? 'text.secondary' : `${color}.main`,
        fontWeight: d.state === 'ok' ? 400 : 700,
      }}>
        {d.pused ?? '—'}% / {d.total_gb}GB
      </Typography>
    </Stack>
  );
}

function WorkloadSection({ s }) {
  const vms = s.vms || [];
  const conts = s.containers || [];
  const errs = s.workload_errors || [];
  if (!vms.length && !conts.length && !errs.length) return null;
  const CAP = 14;
  return (
    <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 1, display: 'flex', flexDirection: 'column', gap: 0.6 }}>
      {vms.length > 0 && (
        <>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary' }}>
            <DnsIcon sx={{ fontSize: 12, verticalAlign: -2, mr: 0.3 }} />VM {vms.length}
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={0.5}>
            {vms.map((v) => {
              const on = /run/i.test(v.state || '');
              return (
                <Chip key={v.name} size="small"
                  label={`${v.name}${v.mem_gb ? ` · ${v.mem_gb}GB` : ''}`}
                  sx={{ height: 20, fontSize: 11, bgcolor: 'action.hover',
                    color: on ? 'success.main' : 'text.disabled',
                    border: 1, borderColor: on ? 'success.main' : 'divider' }} />
              );
            })}
          </Stack>
        </>
      )}
      {conts.length > 0 && (
        <>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary' }}>
            <StorageIcon sx={{ fontSize: 12, verticalAlign: -2, mr: 0.3 }} />컨테이너 {conts.length}
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={0.4}>
            {conts.slice(0, CAP).map((c) => (
              <Tooltip key={c.name} title={`${c.image || ''} · ${c.status || ''}`}>
                <Chip size="small" label={c.name}
                  sx={{ height: 19, fontSize: 10.5, bgcolor: 'action.hover',
                    color: /up/i.test(c.status || '') ? 'text.primary' : 'warning.main' }} />
              </Tooltip>
            ))}
            {conts.length > CAP && (
              <Chip size="small" variant="outlined" label={`…외 ${conts.length - CAP}`}
                sx={{ height: 19, fontSize: 10.5 }} />
            )}
          </Stack>
        </>
      )}
      {!vms.length && !conts.length && errs.length > 0 && (
        <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
          워크로드 미수집 — {errs.join('; ')}
        </Typography>
      )}
    </Box>
  );
}

function ServerCard({ s }) {
  const theme = useTheme();
  const st = STATE[s.state] || STATE.ok;
  const bad = st.sev >= 3;

  return (
    <Paper variant="outlined" sx={{
      p: 2, height: '100%', display: 'flex', flexDirection: 'column', gap: 1.2,
      borderColor: bad ? `${st.color}.main` : undefined,
      borderWidth: bad ? 2 : 1,
    }}>
      <Stack direction="row" flexWrap="wrap" alignItems="baseline" gap={1}>
        <Typography sx={{ fontSize: 16, fontWeight: 700, letterSpacing: '-0.01em' }}>
          {s.host}
        </Typography>
        <Typography sx={{ fontSize: 12, fontFamily: 'monospace', color: 'text.disabled' }}>
          {s.ip || '—'}
        </Typography>
        <Box sx={{ flex: 1 }} />
        {s.classification && (
          <Tooltip title={CLASS_HINT[s.classification] || ''}>
            <Chip size="small" variant="outlined" label={s.classification}
              sx={{ height: 19, fontSize: 10, fontWeight: 700 }} />
          </Tooltip>
        )}
        <Chip size="small" color={st.color} variant={bad ? 'filled' : 'outlined'}
          label={st.label} sx={{ height: 20, fontSize: 11, fontWeight: 700 }} />
      </Stack>

      {(s.location || s.os) && (
        <Stack direction="row" flexWrap="wrap" gap={{ xs: 0.5, sm: 1.6 }}
          sx={{ fontSize: 12.5, color: 'text.secondary' }}>
          {s.location && (
            <span><PlaceIcon sx={{ fontSize: 13, verticalAlign: -2, mr: 0.3 }} />{s.location}</span>
          )}
          {s.os && <span>{s.os}</span>}
          {s.cpu_num && <span>{s.cpu_num} vCPU</span>}
          {s.ram_total_gb && <span>{s.ram_total_gb} GB</span>}
          {s.uptime_days !== null && s.uptime_days !== undefined && (
            <span>가동 {s.uptime_days}일</span>
          )}
        </Stack>
      )}

      {/* 구동 중인 서비스 */}
      {s.services?.length > 0 && (
        <Stack direction="row" flexWrap="wrap" gap={0.5}>
          {s.services.map((x) => (
            <Chip key={x} size="small" label={x}
              sx={{ height: 20, fontSize: 11, bgcolor: 'action.hover' }} />
          ))}
        </Stack>
      )}

      {/* VM / 컨테이너 — 어떤 서비스가 어디서 도는가 */}
      <WorkloadSection s={s} />

      {s.monitored ? (
        <>
          <Stack direction="row" spacing={2}>
            <Meter label="CPU 7일" value={s.cpu_7d} />
            <Meter label="메모리 7일" value={s.mem_7d} />
          </Stack>
          {s.disks?.length > 0 && (
            <Stack spacing={0.6}>{s.disks.map((d) => <DiskBar key={d.mount} d={d} />)}</Stack>
          )}
        </>
      ) : (
        // 🔴 회색 '데이터 없음'으로 두지 않는다. 이유를 쓴다.
        <Typography sx={{
          fontSize: 12.5, lineHeight: 1.6, color: 'error.main', fontWeight: 600,
          bgcolor: 'action.hover', px: 1.5, py: 1, borderLeft: 2, borderColor: 'error.main',
        }}>
          모니터링에 등록되어 있지 않아 상태를 알 수 없습니다 — 지표가 비어 있는 것은
          <b> 문제가 없다는 뜻이 아닙니다.</b>
        </Typography>
      )}

      {s.stops_when_down && (
        <Typography sx={{ fontSize: 12.5, lineHeight: 1.6, color: 'text.secondary' }}>
          죽으면 멈추는 것 — <b style={{ color: theme.palette.text.primary }}>{s.stops_when_down}</b>
        </Typography>
      )}

      <Stack direction="row" flexWrap="wrap" gap={{ xs: 0.5, sm: 1.6 }}
        sx={{ fontSize: 12, color: 'text.disabled', mt: 'auto' }}>
        {s.owner && <span>담당 <b>{String(s.owner).split('@')[0]}</b></span>}
        {s.backup && <span>백업 <b>{s.backup}</b></span>}
      </Stack>

      {s.problems?.length > 0 && (
        <Stack spacing={0.3} sx={{ pt: 0.5 }}>
          {s.problems.slice(0, 4).map((p, i) => (
            <Typography key={i} sx={{
              fontSize: 11.5, color: p.severity >= 4 ? 'error.main' : 'text.secondary',
            }}>
              • {p.name}
            </Typography>
          ))}
          {s.problems.length > 4 && (
            <Typography sx={{ fontSize: 11.5, color: 'text.disabled' }}>
              … 외 {s.problems.length - 4}건
            </Typography>
          )}
        </Stack>
      )}

      {s.note && (
        <Typography sx={{
          fontSize: 12.5, lineHeight: 1.6, color: 'text.secondary',
          bgcolor: 'action.hover', px: 1.5, py: 1,
          borderLeft: 2, borderColor: /🔴/.test(s.note) ? 'error.main' : 'warning.main',
        }}>
          {s.note}
        </Typography>
      )}
    </Paper>
  );
}

export default function InfraPage() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    fetch('/api/infra/status')
      .then(async (r) => {
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw Object.assign(new Error(j.error || `HTTP ${r.status}`), { hint: j.hint });
        return j;
      })
      .then((j) => { if (alive) { setData(j); setLoading(false); } })
      .catch((e) => { if (alive) { setErr(e); setLoading(false); } });
    return () => { alive = false; };
  }, []);

  const sorted = useMemo(() => {
    if (!data?.servers) return [];
    // 나쁜 것부터. 대시보드는 안심시키는 곳이 아니라 눈에 띄게 하는 곳이다.
    return [...data.servers].sort((a, b) => {
      const d = (STATE[b.state]?.sev ?? 0) - (STATE[a.state]?.sev ?? 0);
      return d !== 0 ? d : a.host.localeCompare(b.host);
    });
  }, [data]);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }
  if (err) {
    return (
      <Alert severity="warning" sx={{ my: 3 }}>
        <AlertTitle>{err.message}</AlertTitle>
        {err.hint}
      </Alert>
    );
  }

  const t = data.totals || {};
  const by = t.by_state || {};
  const unmonitored = sorted.filter((s) => !s.monitored);
  const attention = (by.agent_down || 0) + (by.disk_critical || 0) + (by.unregistered || 0);

  return (
    <Box sx={{ pb: 8 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        <DnsIcon color="primary" />
        <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
          사내 서버 현황
        </Typography>
      </Stack>
      <Typography sx={{ color: 'text.secondary', mb: 2.5, maxWidth: 720 }}>
        어떤 서버가 무엇을 구동하고 지금 어떤 상태인가. 선언(자산 대장)과 Zabbix 실측을
        합쳐 주 1회 만듭니다. CPU·메모리는 <b>{data.trend_days ?? 7}일 평균</b>입니다 —
        순간값이 아닙니다.
      </Typography>

      <Grid container spacing={1} sx={{ mb: 3 }}>
        {[
          ['전체 서버', t.servers, false],
          ['감시 중', t.monitored, false],
          ['감시 밖', (t.servers ?? 0) - (t.monitored ?? 0), true],
          ['조치 필요', attention, true],
          ['활성 문제', t.problems, false],
        ].map(([label, val, flag]) => (
          <Grid item xs={6} sm={2.4} key={label}>
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography sx={{
                fontSize: 26, fontWeight: 750, fontFamily: 'monospace', lineHeight: 1.1,
                color: flag && val > 0 ? 'error.main' : 'text.primary',
              }}>
                {val ?? '—'}
              </Typography>
              <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>{label}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* 🔴 이 화면에서 가장 중요한 문장 */}
      {unmonitored.length > 0 && (
        <Alert severity="error" variant="outlined" icon={false} sx={{ mb: 3 }}>
          <AlertTitle sx={{ fontWeight: 750 }}>
            감시 밖에 있는 서버가 {unmonitored.length}대입니다
          </AlertTitle>
          <Typography sx={{ fontSize: 14, lineHeight: 1.7, mb: 1 }}>
            이 서버들은 <b>문제가 없어서 조용한 것이 아니라, 문제를 볼 수단이 없어서
            조용한 것</b>입니다. 디스크가 차거나 프로세스가 죽어도 사용자 신고 전까지
            알 수 없습니다.
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={0.7} sx={{ mb: 1 }}>
            {unmonitored.map((s) => (
              <Chip key={s.host} size="small" color="error" variant="outlined"
                label={`${s.host} — ${s.stops_when_down || '용도 미기재'}`}
                sx={{ height: 22, fontSize: 11.5 }} />
            ))}
          </Stack>
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            통제 <code>A.8.16</code>(모니터링 활동) · <code>C-1</code>(정보자산 목록)
          </Typography>
        </Alert>
      )}

      {data.stale && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <AlertTitle>수집이 오래되었습니다 ({data.age_hours}시간 전)</AlertTitle>
          주간 수집 워크플로가 돌지 않고 있을 수 있습니다. 이 화면의 값은 지금 상태가 아닙니다.
        </Alert>
      )}

      <Grid container spacing={1.5}>
        {sorted.map((s) => (
          <Grid item xs={12} md={6} key={s.host}>
            <ServerCard s={s} />
          </Grid>
        ))}
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Typography sx={{ fontSize: 12, color: 'text.disabled', lineHeight: 1.8 }}>
        CPU·메모리는 <b>7일 트렌드 평균</b>입니다 — 순간값으로 보면 상시 메모리를 점유하는
        가상화 호스트를 유휴로 오독합니다.<br />
        <b>등록 ≠ 감시</b> — 모니터링에 호스트가 있어도 에이전트가 끊겨 있으면 데이터는
        들어오지 않습니다. 그 경우 「감시 두절」로 표시합니다.<br />
        선언 SSOT <code>operations/registers/onprem-assets.yaml</code> · 실측 Zabbix ·
        생성기 <code>scripts/onprem-scan/render_onprem.py</code>
        {data.measured_at && <> · 수집 {String(data.measured_at).slice(0, 16).replace('T', ' ')}</>}
      </Typography>
    </Box>
  );
}
